import React, { useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeftIcon, ChevronRightIcon, ShieldCheckIcon, UsersIcon, BriefcaseIcon } from 'lucide-react';
import { Button } from '../ui/button';
import { ParentCodeSignIn } from './ParentCodeSignIn';
import { schoolCrestUrl, schoolName } from '../../lib/school-brand';
import { safeParentNext } from '../../utils/parentPortal';
import { initialAudience, type SignInAudience } from '../../utils/signInFlow';
import { useAuth } from './AuthContext';
import { entraQueryParams, readScrAppSignIn } from '../../utils/scrappSignIn';

const GoogleIcon = () => (
  <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

const MicrosoftIcon = () => (
  <svg className="h-5 w-5" viewBox="0 0 23 23" aria-hidden="true">
    <path fill="#f35325" d="M0 0h11v11H0z" />
    <path fill="#81bc06" d="M12 0h11v11H12z" />
    <path fill="#05a6f0" d="M0 12h11v11H0z" />
    <path fill="#ffba08" d="M12 12h11v11H12z" />
  </svg>
);

const fieldCls =
  'block w-full min-h-[48px] rounded-xl border border-border-strong bg-surface-raised px-4 text-[1rem] text-ink-950 focus:outline-none focus-visible:ring-2 focus-visible:ring-focus';

/**
 * /login (#387): parents first. Two large choices: the parent/guardian emailed-code
 * flow (inline), or staff Microsoft sign-in. Google and email + password are kept,
 * tucked under "Other sign-in options". All auth calls are unchanged.
 * `defaultAudience` lets /parent/sign-in open straight on the parent path.
 */
export const Login = ({ defaultAudience }: { defaultAudience?: SignInAudience } = {}) => {
  const [params] = useSearchParams();
  const [audience, setAudience] = useState<SignInAudience>(() => defaultAudience ?? initialAudience(window.location.search));
  const parentNext = safeParentNext(params.get('next'));
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { signIn, signInWithSSO, error, user, loading } = useAuth();
  const navigate = useNavigate();

  // Redirect if already logged in
  useEffect(() => {
    // /parent/sign-in never bounces a signed-in staff browser away from the family flow.
    if (user && defaultAudience !== 'parent') {
      if (user.role === 'staff') {
        navigate('/staff');
      } else if (user.role === 'parent') {
        navigate('/parent');
      } else if (user.role === 'teacher') {
        navigate('/teacher');
      }
    }
  }, [user, navigate, defaultAudience]);

  // From the SCR App (?sso=entra#login_hint=…): strip both from the address bar and history so Back can't loop,
  // then, once the session check finds nobody signed in, go straight to Microsoft with the College email.
  const [scrApp] = useState(() => readScrAppSignIn(window.location.search, window.location.hash));
  const autoStarted = useRef(false);
  useEffect(() => {
    if (scrApp) window.history.replaceState(null, '', '/login');
  }, [scrApp]);
  useEffect(() => {
    if (!scrApp || loading || user || autoStarted.current) return;
    autoStarted.current = true;
    signInWithSSO('azure', entraQueryParams(scrApp)).catch(() => {
      // AuthContext has already set its error; the buttons stay available.
    });
  }, [scrApp, loading, user, signInWithSSO]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await signIn(email, password);
      // Navigation will happen in the useEffect above once user state updates
    } catch (err) {
      console.error('Login failed:', err);
      setIsLoading(false);
    }
  };

  const handleSSOLogin = async (provider: 'google' | 'microsoft' | 'azure') => {
    setIsLoading(true);
    try {
      await signInWithSSO(provider);
      // SSO redirect will happen automatically
    } catch (err) {
      console.error('SSO login failed:', err);
      setIsLoading(false);
    }
  };

  const crest = schoolCrestUrl();

  const choice = (key: SignInAudience, icon: ReactNode, title: string, hint: string) => (
    <button
      type="button"
      onClick={() => setAudience(key)}
      className="flex w-full min-h-[88px] items-center gap-4 rounded-2xl border border-border-strong bg-surface-raised p-4 text-left hover:bg-surface-sunken focus:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2"
    >
      <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-surface-sunken text-ink-950" aria-hidden="true">
        {icon}
      </span>
      <span className="flex-1">
        <span className="block text-lg font-semibold text-ink-950">{title}</span>
        <span className="mt-0.5 block text-[1rem] text-ink-600">{hint}</span>
      </span>
      <ChevronRightIcon className="h-6 w-6 flex-shrink-0 text-ink-600" aria-hidden="true" />
    </button>
  );

  const back = (
    <button
      type="button"
      onClick={() => setAudience('choose')}
      className="-ml-2 mb-4 inline-flex min-h-[44px] items-center gap-1.5 rounded-pill px-2 text-[1rem] font-medium text-ink-700 hover:text-ink-950 focus:outline-none focus-visible:ring-2 focus-visible:ring-focus"
    >
      <ArrowLeftIcon className="h-5 w-5" aria-hidden="true" />
      Back
    </button>
  );

  return (
    <div className="min-h-screen bg-surface-sunken px-4 py-8 sm:py-14">
      <div className="mx-auto w-full max-w-md">
        <header className="mb-6 flex items-center gap-3">
          {crest ? (
            <img src={crest} alt="" className="h-12 w-12 shrink-0 object-contain" />
          ) : (
            <span className="h-12 w-1.5 shrink-0 rounded-pill" style={{ background: 'var(--px-tenant-accent)' }} aria-hidden="true" />
          )}
          <div>
            <p className="text-[1rem] font-semibold text-ink-950">{schoolName()}</p>
            <p className="text-[1rem] text-ink-600">Learning Diversity</p>
          </div>
        </header>

        <main className="rounded-2xl bg-surface-raised p-5 shadow-sm sm:p-8">
          {audience === 'choose' && (
            <>
              <h1 className="font-title text-3xl text-ink-950">Sign in</h1>
              <p className="mt-2 text-[1rem] text-ink-700">Choose the option that fits you.</p>
              <div className="mt-6 space-y-3">
                {choice('parent', <UsersIcon className="h-6 w-6" />, "I'm a parent or guardian", 'Get a code by email. No password needed.')}
                {choice('staff', <BriefcaseIcon className="h-6 w-6" />, "I'm a staff member", 'Sign in with your school Microsoft account.')}
              </div>
            </>
          )}

          {audience === 'parent' && (
            <>
              {back}
              <h1 className="font-title text-3xl text-ink-950">View your child&apos;s documents</h1>
              <p className="mt-2 mb-6 text-[1rem] text-ink-700">
                For parents and guardians. We&apos;ll email you a one-time code, so there&apos;s no password to remember.
              </p>
              <ParentCodeSignIn next={parentNext} />
            </>
          )}

          {audience === 'staff' && (
            <>
              {back}
              <h1 className="font-title text-3xl text-ink-950">Staff sign in</h1>
              <p className="mt-2 text-[1rem] text-ink-700">Use your school Microsoft account.</p>

              {error && (
                <div className="mt-5 rounded-xl border border-critical bg-critical-quiet p-3 text-[1rem] text-ink-950" role="alert">
                  {error}
                </div>
              )}

              <Button
                type="button"
                size="lg"
                className="mt-6 w-full min-h-[52px]"
                onClick={() => handleSSOLogin('microsoft')}
                disabled={isLoading}
              >
                <MicrosoftIcon />
                {isLoading ? 'Signing in…' : 'Sign in with Microsoft'}
              </Button>

              <details className="mt-8 rounded-xl border border-border p-4">
                <summary className="min-h-[44px] cursor-pointer text-[1rem] font-medium text-ink-700 flex items-center">
                  Other sign-in options
                </summary>
                <div className="mt-4 space-y-4">
                  <Button type="button" variant="outline" className="w-full min-h-[48px] text-[1rem]" onClick={() => handleSSOLogin('google')} disabled={isLoading}>
                    <GoogleIcon />
                    Sign in with Google
                  </Button>
                  <form className="space-y-3" onSubmit={handleSubmit}>
                    <div>
                      <label htmlFor="email-address" className="mb-1 block text-[1rem] font-medium text-ink-950">Email address</label>
                      <input id="email-address" name="email" type="email" autoComplete="email" required className={fieldCls}
                        value={email} onChange={(e) => setEmail(e.target.value)} disabled={isLoading} />
                    </div>
                    <div>
                      <label htmlFor="password" className="mb-1 block text-[1rem] font-medium text-ink-950">Password</label>
                      <input id="password" name="password" type="password" autoComplete="current-password" required className={fieldCls}
                        value={password} onChange={(e) => setPassword(e.target.value)} disabled={isLoading} />
                    </div>
                    <Button type="submit" variant="outline" className="w-full min-h-[48px] text-[1rem]" disabled={isLoading}>
                      {isLoading ? 'Signing in…' : 'Sign in with email'}
                    </Button>
                  </form>
                </div>
              </details>
              <p className="mt-6 text-[1rem] text-ink-600">Need help signing in? Contact the IT helpdesk.</p>
            </>
          )}
        </main>

        {audience !== 'staff' && (
        <p className="mt-6 flex gap-2 text-[1rem] text-ink-700">
          <ShieldCheckIcon className="mt-0.5 h-5 w-5 flex-shrink-0" aria-hidden="true" />
          <span>
            Your information stays private. Only you and the school staff who support your child can see these
            documents.
          </span>
        </p>
        )}
      </div>
    </div>
  );
};
